import type { Session } from '@ory/client-fetch';
import { OryError, useOryAuth } from '@/hooks/useOryAuth';
import type { AuthProvider, User, SessionChange, AccessContext, AccessRequirement } from '../AuthProvider';
import { jwtDecode } from 'jwt-decode';
import { watch } from 'vue';

export class OryAuth implements AuthProvider {
  private readonly auth = useOryAuth();
  private onSessionChange?: SessionChange;
  private cachedToken: {
    value: string;
    expiresAt: number;
    sessionId: string;
    aal: string | undefined;
  } | null = null;
  private sessionFetchPromise: Promise<Session | null> | null = null;
  private mfaEnabled: boolean = false;

  async init(onSessionChange: SessionChange): Promise<() => void> {
    this.onSessionChange = onSessionChange;

    const unwatch = watch(
      () => this.auth.session.value,
      (newSession, oldSession) => {
        const newId = newSession?.identity?.id;
        const oldId = oldSession?.identity?.id;

        // Only broadcast if the actual user identity changes (login, logout, account switch)
        if (newId !== oldId) {
          const user = newSession?.active && newSession.identity ? this.mapUser(newSession) : null;
          this.onSessionChange?.(user);
        }
      },
    );

    return () => {
      unwatch();
      this.onSessionChange = undefined;
    };
  }

  async getCurrentUser(): Promise<User | null> {
    if (!this.sessionFetchPromise) {
      this.sessionFetchPromise = this.auth.checkSession().finally(() => {
        this.sessionFetchPromise = null;
      });
    }

    const session = await this.sessionFetchPromise;
    return session?.active && session.identity ? this.mapUser(session) : null;
  }

  async getToken(): Promise<string | null> {
    const current = this.auth.session.value;
    const cached = this.cachedToken;

    if (
      current?.active &&
      cached?.sessionId === current.id &&
      cached.aal === current.authenticator_assurance_level &&
      Date.now() < cached.expiresAt
    ) {
      return cached.value;
    }

    this.cachedToken = null;
    try {
      const session = await this.auth.sessionToJWT({ tokenizeAs: 'jwt_template' });
      if (!session?.active) {
        this.auth.session.value = null;
        return null;
      }
      if (!session.tokenized) throw new Error('Ory did not return a tokenized JWT');

      const { exp } = jwtDecode<{ exp?: number }>(session.tokenized);
      const expiresAt = typeof exp === 'number' ? exp * 1_000 : 0;

      if (expiresAt > Date.now()) {
        this.cachedToken = {
          value: session.tokenized,
          expiresAt,
          sessionId: session.id,
          aal: session.authenticator_assurance_level,
        };
      }
      return session.tokenized;
    } catch (error) {
      if (error instanceof OryError && error.status === 401) {
        this.auth.session.value = null;
        return null;
      }
      throw error;
    }
  }

  async checkAccess(context: AccessContext): Promise<AccessRequirement | null> {
    const session = this.auth.session.value;
    if (!session?.active || !session.identity) {
      throw new Error('Ory session is unavailable');
    }

    if (context.passwordResetRecommended) {
      return 'reset-password';
    }

    const hasPassword = session.authentication_methods?.some((m) => m.method === 'password');
    const isRecoverySession = session.authentication_methods?.some((m) => m.method?.includes('recovery'));

    if (isRecoverySession && !hasPassword) {
      return 'reset-password';
    }

    const email = this.emailFrom(session);
    const verified = session.identity.verifiable_addresses?.some(
      (address) => address.via === 'email' && address.value === email && address.verified,
    );

    if (!verified) return 'verify-email';

    const mfaRequirement = await this.checkMFA();
    if (mfaRequirement) {
      return mfaRequirement;
    }

    return null;
  }

  async checkMFA(): Promise<'enroll-mfa' | 'submit-mfa' | null> {
    if (!this.hasMFAEnabled()) return null;

    const session = this.auth.session.value;
    if (!session?.active || !session.identity) {
      throw new Error('Ory session is unavailable');
    }

    if (this.hasMFAEnabled() && session.authenticator_assurance_level !== 'aal2') {
      return session.identity.credentials?.totp ? 'submit-mfa' : 'enroll-mfa';
    }

    return null;
  }

  hasMFAEnabled(): boolean {
    return this.mfaEnabled;
  }

  async logout(): Promise<void> {
    const flow = await this.auth.createLogoutFlow();
    if (!flow.logout_token) {
      throw new Error('Ory did not return a logout token');
    }

    await this.auth.updateLogoutFlow({ token: flow.logout_token });

    this.cachedToken = null;
    this.auth.session.value = null;
  }

  private mapUser(session: Session): User {
    if (!session.identity) {
      throw new Error('Ory session has no identity');
    }

    return {
      id: session.identity.id,
      email: this.emailFrom(session),
    };
  }

  private emailFrom(session: Session): string {
    const email = (session.identity?.traits as { email?: unknown } | undefined)?.email;
    if (typeof email !== 'string') {
      throw new Error('Ory identity has no email');
    }
    return email;
  }
}
